import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import Loading from '../../loading/loadingPage'
import UnsavedChanges from '../../../components/UnsavedChanges/UnsavedChanges'
import useLoadListBuilder from './builder/useLoadListBuilder'
import Palette from './builder/Palette'
import SectionNode from './builder/SectionNode'
import ItemChip from './builder/ItemChip'
import PropertiesPanel from './builder/PropertiesPanel'
import AppliesToPanel from './builder/AppliesToPanel'
import {
  COLOURS,
  KIND,
  SOURCE,
  TYPE_OPTIONS,
  chipIdFromDndId,
  rowForKind,
  sectionIdFromDndId,
  sectionSortId,
} from './builder/chipTypes'

const LIST_ROUTE = '/make-ready-load-lists'

const isSectionSortId = (id) => String(id).startsWith('section-sort:')

const LoadListBuilder = () => {
  const { loadListID } = useParams()
  const [searchParams] = useSearchParams()
  const cloneFromId = searchParams.get('cloneFrom')
  const prefillType = searchParams.get('type')
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)

  const canEdit =
    user?.employee?.teams?.includes('Governance') ||
    user?.employee?.teams?.includes('Manager') ||
    user?.employee?.teams?.includes('Supervisor')
  const readOnly = !canEdit

  const b = useLoadListBuilder({ loadListID, cloneFromId, prefillType })

  const [activeDrag, setActiveDrag] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [issues, setIssues] = useState(null)
  const [titleError, setTitleError] = useState('')
  const [titleTouched, setTitleTouched] = useState(false)

  // Warn on tab close / hard navigation while there are unsaved edits
  useEffect(() => {
    if (!b.dirty || readOnly) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [b.dirty, readOnly])

  // ---- dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Sections reorder among themselves; everything else (palette drops and
  // chip moves) resolves against chips + section drop zones only.
  const collisionDetection = useCallback((args) => {
    const source = args.active?.data?.current?.source
    if (source === SOURCE.SECTION_HANDLE) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((c) =>
          isSectionSortId(c.id),
        ),
      })
    }
    const containers = args.droppableContainers.filter(
      (c) => !isSectionSortId(c.id),
    )
    const within = pointerWithin({ ...args, droppableContainers: containers })
    if (within.length) return within
    return rectIntersection({ ...args, droppableContainers: containers })
  }, [])

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null)
    if (!over || readOnly) return
    const data = active.data.current || {}

    if (data.source === SOURCE.SECTION_HANDLE) {
      const overSectionId = sectionIdFromDndId(over.id)
      if (overSectionId && overSectionId !== data.sectionId) {
        b.reorderSections(data.sectionId, overSectionId)
      }
      return
    }

    const targetSectionId = sectionIdFromDndId(over.id)
    if (!targetSectionId) return
    const overChipId = chipIdFromDndId(over.id)
    const overKind = over.data.current?.kind
    const beforeChipId =
      overChipId && overKind && rowForKind(overKind) === rowForKind(data.kind)
        ? overChipId
        : null

    if (data.source === SOURCE.PALETTE) {
      b.addChip(targetSectionId, data.kind, data.payload, beforeChipId)
    } else if (data.source === SOURCE.SECTION) {
      b.moveChip({
        fromSectionId: data.sectionId,
        chipId: data.chipId,
        toSectionId: targetSectionId,
        beforeChipId,
      })
    }
  }

  // ---- Palette "New consumable" → straight into the selected/first section
  const handleNewConsumable = (payload) => {
    let target = b.activeSectionId || b.sections[0]?.clientId
    if (!target) target = b.addSection('General')
    b.addChip(target, KIND.CONSUMABLE, payload)
  }

  // ---- Save / discard / delete
  const handleSave = async () => {
    if (readOnly) return
    setTitleTouched(true)
    const found = b.validate()
    if (found.length) {
      setIssues(found)
      return
    }
    const result = await b.save()
    if (result.ok) {
      setTitleError('')
      setToast({ message: 'Load list saved' })
      if (b.isNew) navigate(LIST_ROUTE)
    } else {
      if (/title|name|exists|duplicate/i.test(result.error || '')) {
        setTitleError(result.error)
      }
      setToast({ message: result.error, severity: 'error' })
    }
  }

  const handleDelete = async () => {
    const result = await b.deleteList()
    setDeleteOpen(false)
    if (result.ok) {
      setToast({ message: 'Load list deleted' })
      navigate(LIST_ROUTE)
    } else {
      setToast({ message: result.error, severity: 'error' })
    }
  }

  // ---- Header bits
  const typeOptions = useMemo(() => {
    if (b.type && !TYPE_OPTIONS.includes(b.type))
      return [...TYPE_OPTIONS, b.type]
    return TYPE_OPTIONS
  }, [b.type])

  const showTitleError = titleError || (titleTouched && !b.title.trim())
  const changesCaption = !b.dirty
    ? ''
    : b.contentDirty
      ? `Unsaved changes${
          b.assignmentChanges
            ? ` · ${b.assignmentChanges} assignment change${b.assignmentChanges === 1 ? '' : 's'}`
            : ''
        }`
      : `${b.assignmentChanges} assignment change${b.assignmentChanges === 1 ? '' : 's'} not saved`

  const appliesNoun = b.type === 'Vehicle' ? 'vehicle' : 'item'

  if (b.loading) return <Loading />

  if (b.fetchError) {
    return (
      <Stack gap={2}>
        <Button
          component={RouterLink}
          to={LIST_ROUTE}
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Load Lists
        </Button>
        <Alert severity="error" variant="filled">
          Unable to fetch this load list. Please try again later.
        </Alert>
      </Stack>
    )
  }

  const overlayContent = (() => {
    if (!activeDrag) return null
    if (activeDrag.source === SOURCE.SECTION_HANDLE) {
      return (
        <Paper
          elevation={6}
          sx={{ px: 1.5, py: 1, borderRadius: '5px', maxWidth: 320 }}
        >
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {activeDrag.section?.sectionName || 'Section'}
          </Typography>
        </Paper>
      )
    }
    return (
      <ItemChip
        kind={activeDrag.kind}
        chip={activeDrag.chip || activeDrag.payload}
        overlay
        readOnly
      />
    )
  })()

  return (
    <Stack gap={2}>
      {readOnly && (
        <Alert severity="info">
          You can view this load list but only Governance, Manager or Supervisor
          team members can change it.
        </Alert>
      )}
      {b.archived && (
        <Alert severity="warning">This load list is archived.</Alert>
      )}

      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={2}
        flexWrap="wrap"
      >
        <Button
          component={RouterLink}
          to={LIST_ROUTE}
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
        >
          Load Lists
        </Button>
        <TextField
          variant="standard"
          placeholder="Load list title"
          value={b.title}
          required
          disabled={readOnly}
          error={!!showTitleError}
          helperText={
            titleError || (showTitleError ? 'A title is required' : ' ')
          }
          onBlur={() => setTitleTouched(true)}
          onChange={(e) => {
            setTitleError('')
            b.setTitle(e.target.value)
          }}
          inputProps={{
            style: { fontSize: '1.25rem', fontWeight: 700 },
            'aria-label': 'Load list title',
          }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={readOnly}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={b.type}
            onChange={(e) => b.setType(e.target.value)}
          >
            {typeOptions.map((t) => (
              <MenuItem
                key={t}
                value={t}
                sx={
                  TYPE_OPTIONS.includes(t)
                    ? {}
                    : { color: 'text.disabled', fontStyle: 'italic' }
                }
              >
                {TYPE_OPTIONS.includes(t) ? t : `${t} (legacy)`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!readOnly && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ ml: { md: 'auto' } }}
          >
            <Typography
              variant="caption"
              color={b.dirty ? 'warning.main' : 'text.secondary'}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {changesCaption}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={!b.dirty || b.saving}
              onClick={() => setDiscardOpen(true)}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={b.saving || (!b.isNew && !b.dirty)}
              onClick={handleSave}
            >
              Save load list
            </Button>
            {!b.isNew && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-label="More"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  open={!!menuAnchor}
                  anchorEl={menuAnchor}
                  onClose={() => setMenuAnchor(null)}
                >
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => {
                      setMenuAnchor(null)
                      setDeleteOpen(true)
                    }}
                  >
                    Delete load list
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        )}
      </Stack>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={({ active }) => setActiveDrag(active.data.current || null)}
        onDragCancel={() => setActiveDrag(null)}
        onDragEnd={handleDragEnd}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'start',
            gridTemplateColumns: {
              xs: '1fr',
              md: '260px minmax(0, 1fr)',
              lg: '280px minmax(0, 1fr) 300px',
            },
          }}
        >
          {/* Palette */}
          <Box sx={{ minWidth: 0 }}>
            <Palette
              readOnly={readOnly}
              onAddConsumable={handleNewConsumable}
              setToast={setToast}
            />
          </Box>

          {/* Canvas */}
          <Box
            sx={{
              minWidth: 0,
              p: 2,
              border: '1px solid',
              borderColor: COLOURS.divider,
              borderRadius: '5px',
              bgcolor: '#FCFCFD',
              backgroundImage:
                'radial-gradient(#E2E8F0 1px, transparent 1.1px)',
              backgroundSize: '16px 16px',
              minHeight: 420,
            }}
            onClick={() => b.setSelection(null)}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderLeft: `6px solid ${COLOURS.teal}`,
                border: '1px solid',
                borderColor: COLOURS.divider,
                borderLeftWidth: 6,
                borderLeftColor: COLOURS.teal,
                borderRadius: '5px',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {b.title.trim() || 'Untitled load list'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {b.type || 'No type'} · {b.counts.sections} section
                {b.counts.sections === 1 ? '' : 's'} · {b.counts.consumables}{' '}
                consumable{b.counts.consumables === 1 ? '' : 's'} ·{' '}
                {b.counts.assets} expected asset
                {b.counts.assets === 1 ? '' : 's'} · applies to{' '}
                {b.counts.appliesTo} {appliesNoun}
                {b.counts.appliesTo === 1 ? '' : 's'}
              </Typography>
            </Paper>

            <Box
              sx={{
                ml: 3,
                pt: 2,
                borderLeft: `2px solid ${COLOURS.trunk}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <SortableContext
                items={b.sections.map((s) => sectionSortId(s.clientId))}
                strategy={verticalListSortingStrategy}
              >
                <Stack gap={2}>
                  {b.sections.map((section, index) => (
                    <SectionNode
                      key={section.clientId}
                      section={section}
                      index={index}
                      readOnly={readOnly}
                      isActive={b.activeSectionId === section.clientId}
                      selectedChipId={
                        b.selection?.sectionId === section.clientId
                          ? b.selection.chipId
                          : null
                      }
                      onSelectChip={(sel) => {
                        b.setSelection(sel)
                        b.setActiveSectionId(sel.sectionId)
                      }}
                      onActivate={(id) => b.setActiveSectionId(id)}
                      onRename={(id, v) =>
                        b.updateSection(id, { sectionName: v })
                      }
                      onSetType={(id, v) =>
                        b.updateSection(id, { sectionType: v })
                      }
                      onRemove={b.removeSection}
                    />
                  ))}
                </Stack>
              </SortableContext>

              {b.sections.length === 0 && (
                <Stack direction="row">
                  <Box
                    sx={{
                      width: 20,
                      flex: '0 0 20px',
                      alignSelf: 'flex-start',
                      mt: 3,
                      borderTop: `2px solid ${COLOURS.trunk}`,
                    }}
                  />
                  <Alert severity="info" sx={{ flex: 1 }}>
                    This list has no sections yet. Add a section (a pouch,
                    drawer or cupboard), then drag items into it from the
                    palette.
                  </Alert>
                </Stack>
              )}

              {!readOnly && (
                <Stack direction="row" mt={2}>
                  <Box
                    sx={{
                      width: 20,
                      flex: '0 0 20px',
                      alignSelf: 'flex-start',
                      mt: 2.5,
                      borderTop: `2px solid ${COLOURS.trunk}`,
                    }}
                  />
                  <Button
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() =>
                      b.addSection(`Section ${b.sections.length + 1}`)
                    }
                    sx={{
                      justifyContent: 'flex-start',
                      border: '1px dashed',
                      borderColor: COLOURS.trunk,
                      borderRadius: '5px',
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                      py: 1.25,
                      '&:hover': {
                        borderColor: COLOURS.teal,
                        color: COLOURS.teal,
                      },
                    }}
                  >
                    Add section
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>

          {/* Right panel */}
          <Stack
            gap={2}
            sx={{ minWidth: 0, gridColumn: { md: '1 / -1', lg: 'auto' } }}
          >
            <PropertiesPanel
              selected={b.selectedChip}
              readOnly={readOnly}
              onUpdate={(patch) =>
                b.selectedChip &&
                b.updateChip(
                  b.selectedChip.section.clientId,
                  b.selectedChip.chip.clientId,
                  patch,
                )
              }
              onRemove={() =>
                b.selectedChip &&
                b.removeChip(
                  b.selectedChip.section.clientId,
                  b.selectedChip.chip.clientId,
                )
              }
            />
            <AppliesToPanel
              loadListID={loadListID}
              type={b.type}
              assignments={b.assignments}
              readOnly={readOnly}
              onAdd={b.addAssignment}
              onRemove={b.removeAssignment}
            />
          </Stack>
        </Box>

        <DragOverlay dropAnimation={null}>{overlayContent}</DragOverlay>
      </DndContext>

      {!readOnly && (
        <UnsavedChanges
          changesPresent={b.dirty}
          handleSave={handleSave}
          saveButtonText="Save load list"
        />
      )}

      {/* Validation issues */}
      <Dialog open={!!issues} onClose={() => setIssues(null)}>
        <DialogTitle>Fix these before saving</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Nothing has been dropped or changed. Resolve the following and save
            again:
            <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 0 }}>
              {(issues || []).map((i) => (
                <li key={i}>{i}</li>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIssues(null)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard */}
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The load list will go back to how it was last saved
            {b.assignmentChanges
              ? `, including ${b.assignmentChanges} pending assignment change${b.assignmentChanges === 1 ? '' : 's'}`
              : ''}
            .
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDiscardOpen(false)
              setTitleError('')
              setTitleTouched(false)
              b.discard()
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this load list?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {b.counts.appliesTo > 0
              ? `It is assigned to ${b.counts.appliesTo} ${appliesNoun}${b.counts.appliesTo === 1 ? '' : 's'} (${b.assignments
                  .filter((a) => a.pending !== 'remove')
                  .map((a) => a.label)
                  .join(
                    ', ',
                  )}). Deleting it unassigns them - they will need a new list before they can be checked again. `
              : 'Any vehicles or assets pointing at this list will be unassigned. '}
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default LoadListBuilder
